import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Platform,
  Alert,
  Dimensions,
  Modal,
  ActivityIndicator,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { Image } from 'expo-image';
import * as ImagePicker from 'expo-image-picker';
import MaterialIcons from '@expo/vector-icons/MaterialIcons';
import { useTheme } from '../context/ThemeContext';
import { useUserPreferences } from '../context/UserPreferencesContext';
import { isUSUserFlag } from '../utils/dateFormat';
import HamburgerMenu from '../components/HamburgerMenu';
import Stopwatch from '../components/Stopwatch';
import { getApiUrl } from '../utils/apiConfig';
import {
  setUserSnapshot,
  getLoggedInStudentId,
  isSuperUser,
  isDemoMode,
  normalizeStudentUserRecord,
  showGettingStartedFlag,
  weightInKgFlag,
  focusExercisesFromUser,
  focusReportingPositionFromUser,
} from '../utils/sessionUser';
import TwoFactorResetModal from '../components/TwoFactorResetModal';
import StudentGallerySection from '../components/StudentGallerySection';
import FocusExercisePickerRow from '../components/FocusExercisePickerRow';
import { dedupeCoursesForDropdown } from '../utils/coursesCatalog';
import { invalidateStudentProfilePictureCache } from '../utils/studentProfilePhoto';
import {
  getSupabaseClient,
  getProfilePictureBucketName,
  getGradeImageBucketName,
  getSupabaseConfigHint,
} from '../utils/supabase';

const { width } = Dimensions.get('window');
const MAX_FOCUS_EXERCISES = 5;

function sanitizeProfileFilename(name) {
  const raw = String(name || '').replace(/^.*[/\\]/, '');
  const cleaned = raw.replace(/[^a-zA-Z0-9._-]+/g, '_').replace(/^_+|_+$/g, '');
  return (cleaned || 'photo').slice(0, 180);
}

function guessMimeFromName(name) {
  const lower = String(name).toLowerCase();
  if (lower.endsWith('.png')) return 'image/png';
  if (lower.endsWith('.webp')) return 'image/webp';
  if (lower.endsWith('.gif')) return 'image/gif';
  if (lower.endsWith('.heic')) return 'image/heic';
  if (lower.endsWith('.jpeg')) return 'image/jpeg';
  return 'image/jpeg';
}

async function uriToArrayBuffer(uri) {
  const res = await fetch(uri);
  if (!res.ok) throw new Error(`Could not read image (${res.status})`);
  return res.arrayBuffer();
}

export default function ProfilePage({ navigation, route }) {
  const theme = useTheme();
  const { setIsUSUserFromUser, setWeightInKgFromUser } = useUserPreferences();
  const stopwatchRef = useRef(null);
  const [user, setUser] = useState(null);
  const [profileImage, setProfileImage] = useState(null);
  const [pendingProfile, setPendingProfile] = useState(null);
  const [profilePicSaving, setProfilePicSaving] = useState(false);
  const [profilePicJustSaved, setProfilePicJustSaved] = useState(false);
  const [breaksRemaining, setBreaksRemaining] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showGradePhotoModal, setShowGradePhotoModal] = useState(false);
  const [pendingGrade, setPendingGrade] = useState(null);
  const [gradeImageSaving, setGradeImageSaving] = useState(false);
  const [gradeJustSaved, setGradeJustSaved] = useState(false);
  const [loggedInId, setLoggedInId] = useState(null);
  const [show2faResetModal, setShow2faResetModal] = useState(false);
  const [reset2faBusy, setReset2faBusy] = useState(false);
  const [settingsExpanded, setSettingsExpanded] = useState(false);
  const [draftIsUSUser, setDraftIsUSUser] = useState(false);
  const [draftShowGettingStarted, setDraftShowGettingStarted] = useState(true);
  const [draftWeightInKg, setDraftWeightInKg] = useState(true);
  const [draftDemoMode, setDraftDemoMode] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [focusModeExpanded, setFocusModeExpanded] = useState(false);
  const [focusModeSaving, setFocusModeSaving] = useState(false);
  const [draftFocusSlots, setDraftFocusSlots] = useState([null]);
  const [draftFocusReportingPosition, setDraftFocusReportingPosition] = useState('top');
  const [focusCourses, setFocusCourses] = useState([]);

  useEffect(() => {
    const userId = route?.params?.userId || 1;
    fetchUser(userId);
    void getLoggedInStudentId().then((id) => setLoggedInId(id != null ? Number(id) : null));
    void fetch(`${getApiUrl()}/api/courses`)
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => setFocusCourses(dedupeCoursesForDropdown(Array.isArray(data) ? data : [])))
      .catch(() => setFocusCourses([]));
  }, [route]);

  const fetchUser = async (userId) => {
    try {
      const response = await fetch(`${getApiUrl()}/api/users/${userId}`);
      if (response.ok) {
        const data = await response.json();
        const normalized = normalizeStudentUserRecord(data);
        setUser(normalized);
        setIsUSUserFromUser(normalized);
        setProfileImage(normalized.profile_picture_url || null);
        fetchBreaksRemaining(normalized);
      }
    } catch (error) {
      console.error('Error fetching user:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBreaksRemaining = async (userData) => {
    if (!userData?.student_grade) {
      setBreaksRemaining(null);
      return;
    }

    try {
      // Get current grade rank
        const gradeResponse = await fetch(`${getApiUrl()}/api/grades`);
      if (gradeResponse.ok) {
        const grades = await gradeResponse.json();
        const currentGrade = grades.find(g => g.grade_name === userData.student_grade);
        if (!currentGrade) {
          setBreaksRemaining(null);
          return;
        }

        const nextGradeRank = currentGrade.grade_rank_order + 1;
        const nextGrade = grades.find(g => g.grade_rank_order === nextGradeRank);
        
        if (!nextGrade) {
          setBreaksRemaining('Max Grade');
          return;
        }

        // Get total breaks required for next grade
          const breaksResponse = await fetch(`${getApiUrl()}/api/grade-breaks?grade=${encodeURIComponent(nextGrade.grade_name)}`);
        if (breaksResponse.ok) {
          const gradeBreaks = await breaksResponse.json();
          const totalBreaks = gradeBreaks.length;

          // Get user's completed breaks for next grade
          // We need to check user_breaks that are linked to breaks in grade_breaks for next grade
            const userBreaksResponse = await fetch(`${getApiUrl()}/api/user-breaks?student_id=${userData.id}`);
          if (userBreaksResponse.ok) {
            const userBreaks = await userBreaksResponse.json();
            const nextGradeBreakIds = new Set(gradeBreaks.map(gb => gb.break_id));
            const completedBreaks = userBreaks.filter(ub => 
              nextGradeBreakIds.has(ub.break_id) && ub.status === 'completed'
            ).length;
            const remaining = totalBreaks - completedBreaks;
            setBreaksRemaining(remaining >= 0 ? remaining : 0);
          } else {
            setBreaksRemaining(totalBreaks);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching breaks remaining:', error);
      setBreaksRemaining(null);
    }
  };

  const requestImagePickerPermission = async () => {
    if (Platform.OS !== 'web') {
      const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (status !== 'granted') {
        Alert.alert('Permission needed', 'Sorry, we need camera roll permissions to update your profile picture!');
        return false;
      }
    }
    return true;
  };

  const handlePickImage = async () => {
    const hasPermission = await requestImagePickerPermission();
    if (!hasPermission) return;

    try {
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [1, 1],
        quality: 0.8,
        allowsMultipleSelection: false,
      });

      if (!result.canceled && result.assets && result.assets[0]) {
        const asset = result.assets[0];
        const fileName = asset.fileName || asset.uri?.split('/').pop() || 'photo.jpg';
        setPendingProfile({ uri: asset.uri, fileName });
        setProfilePicJustSaved(false);
      }
    } catch (error) {
      console.error('Error picking image:', error);
      Alert.alert('Error', 'Failed to pick image');
    }
  };

  const handleSaveProfilePicture = async () => {
    if (!pendingProfile || !user?.id) {
      Alert.alert('Error', 'Please select an image first');
      return;
    }

    const supabase = getSupabaseClient();
    if (!supabase) {
      Alert.alert(
        'Upload unavailable',
        `Configure Supabase for uploads:\n${getSupabaseConfigHint().join(', ')}`
      );
      return;
    }

    setProfilePicSaving(true);
    try {
      const bucket = getProfilePictureBucketName();
      const safe = sanitizeProfileFilename(pendingProfile.fileName);
      const path = `${user.id}/${safe}`;
      const body = await uriToArrayBuffer(pendingProfile.uri);
      const { error: upErr } = await supabase.storage.from(bucket).upload(path, body, {
        contentType: guessMimeFromName(pendingProfile.fileName),
        upsert: true,
      });
      if (upErr) {
        throw new Error(upErr.message || 'Storage upload failed');
      }
      const { data: pub } = supabase.storage.from(bucket).getPublicUrl(path);
      const publicUrl = pub?.publicUrl;
      if (!publicUrl) {
        throw new Error('Could not get public URL for profile photo');
      }

      const putRes = await fetch(`${getApiUrl()}/api/users/${encodeURIComponent(String(user.id))}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ profile_picture_url: publicUrl }),
      });
      const putRaw = await putRes.text();
      let putBody = {};
      try {
        putBody = putRaw ? JSON.parse(putRaw) : {};
      } catch {
        /* ignore */
      }
      if (!putRes.ok) {
        throw new Error(putBody.error || 'Failed to save profile URL');
      }

      const updated = { ...user, ...putBody };
      setUser(updated);
      setProfileImage(publicUrl);
      setPendingProfile(null);
      setProfilePicJustSaved(true);
      await setUserSnapshot(updated);
      invalidateStudentProfilePictureCache(updated.id);
    } catch (error) {
      console.error('Error saving profile picture:', error);
      Alert.alert('Error', error.message || 'Failed to save profile picture');
    } finally {
      setProfilePicSaving(false);
    }
  };

  const handlePickGradeImage = async () => {
    const hasPermission = await requestImagePickerPermission();
    if (!hasPermission) return;

    try {
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [1, 1],
        quality: 0.85,
        allowsMultipleSelection: false,
      });

      if (!result.canceled && result.assets && result.assets[0]) {
        const asset = result.assets[0];
        const fileName = asset.fileName || asset.uri?.split('/').pop() || 'grade-image.jpg';
        setPendingGrade({ uri: asset.uri, fileName });
        setGradeJustSaved(false);
      }
    } catch (error) {
      console.error('Error picking grade image:', error);
      Alert.alert('Error', 'Failed to pick image');
    }
  };

  const handleSaveGradeImage = async () => {
    if (!pendingGrade || !user?.id) {
      Alert.alert('Error', 'Please select an image first');
      return;
    }

    const loggedInId = await getLoggedInStudentId();
    const studentId =
      loggedInId != null && !Number.isNaN(Number(loggedInId))
        ? Number(loggedInId)
        : user?.id != null
          ? Number(user.id)
          : null;

    if (studentId == null || Number.isNaN(studentId)) {
      Alert.alert('Error', 'Could not determine your logged-in student account.');
      return;
    }

    if (Number(user.id) !== studentId) {
      Alert.alert(
        'Unavailable',
        'Grade images can only be uploaded for your own profile.'
      );
      return;
    }

    const supabase = getSupabaseClient();
    if (!supabase) {
      Alert.alert(
        'Upload unavailable',
        `Configure Supabase for uploads:\n${getSupabaseConfigHint().join(', ')}`
      );
      return;
    }

    setGradeImageSaving(true);
    try {
      // Supabase Storage (S3-compatible): gradeImageBucket → public object URL → students.grade_url
      const bucket = getGradeImageBucketName();
      const safe = sanitizeProfileFilename(pendingGrade.fileName);
      const path = `${studentId}/${safe}`;
      const body = await uriToArrayBuffer(pendingGrade.uri);
      const { error: upErr } = await supabase.storage.from(bucket).upload(path, body, {
        contentType: guessMimeFromName(pendingGrade.fileName),
        upsert: true,
      });
      if (upErr) {
        throw new Error(upErr.message || 'Storage upload failed');
      }
      const { data: pub } = supabase.storage.from(bucket).getPublicUrl(path);
      const publicUrl = pub?.publicUrl;
      if (!publicUrl) {
        throw new Error('Could not get public URL for grade image');
      }

      const putRes = await fetch(
        `${getApiUrl()}/api/users/${encodeURIComponent(String(studentId))}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ grade_url: publicUrl }),
        }
      );
      const putRaw = await putRes.text();
      let putBody = {};
      try {
        putBody = putRaw ? JSON.parse(putRaw) : {};
      } catch {
        /* ignore */
      }
      if (!putRes.ok) {
        throw new Error(putBody.error || 'Failed to save grade image URL');
      }

      const updated = { ...user, ...putBody, grade_url: putBody.grade_url ?? publicUrl };
      setUser(updated);
      setPendingGrade(null);
      setGradeJustSaved(true);
      await setUserSnapshot(updated);
    } catch (error) {
      console.error('Error saving grade image:', error);
      Alert.alert('Error', error.message || 'Failed to save grade image');
    } finally {
      setGradeImageSaving(false);
    }
  };

  const isOwnProfile =
    loggedInId != null && user?.id != null && Number(loggedInId) === Number(user.id);

  const focusSlotsFromUser = (profileUser) => {
    const entries = focusExercisesFromUser(profileUser);
    if (!entries.length) return [null];
    return entries;
  };

  const handleToggleSettings = () => {
    if (settingsExpanded) {
      setSettingsExpanded(false);
      return;
    }
    setDraftIsUSUser(isUSUserFlag(user));
    setDraftShowGettingStarted(showGettingStartedFlag(user));
    setDraftWeightInKg(weightInKgFlag(user));
    setDraftDemoMode(isDemoMode(user));
    setSettingsExpanded(true);
  };

  const handleSettingsCancel = () => {
    setDraftIsUSUser(isUSUserFlag(user));
    setDraftShowGettingStarted(showGettingStartedFlag(user));
    setDraftWeightInKg(weightInKgFlag(user));
    setDraftDemoMode(isDemoMode(user));
    setSettingsExpanded(false);
  };

  const handleToggleFocusMode = () => {
    if (focusModeExpanded) {
      setFocusModeExpanded(false);
      return;
    }
    setDraftFocusSlots(focusSlotsFromUser(user));
    setDraftFocusReportingPosition(focusReportingPositionFromUser(user));
    setFocusModeExpanded(true);
  };

  const handleFocusModeCancel = () => {
    setDraftFocusSlots(focusSlotsFromUser(user));
    setDraftFocusReportingPosition(focusReportingPositionFromUser(user));
    setFocusModeExpanded(false);
  };

  const handleFocusModeSave = async () => {
    if (!user?.id) return;
    const focusExercises = draftFocusSlots.filter((slot) => slot?.exercise_id);
    setFocusModeSaving(true);
    try {
      const res = await fetch(`${getApiUrl()}/api/users/${encodeURIComponent(String(user.id))}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          focusExercises,
          focusReportingPosition: draftFocusReportingPosition,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        Alert.alert('Could not save', data.error || 'Failed to update focus exercises');
        return;
      }
      const updated = normalizeStudentUserRecord({
        ...user,
        ...data,
        focusExercises,
        focus_exercises: focusExercises,
        focusReportingPosition: draftFocusReportingPosition,
        focus_reporting_position: draftFocusReportingPosition,
      });
      setUser(updated);
      await setUserSnapshot(updated);
      setDraftFocusSlots(focusSlotsFromUser(updated));
      setFocusModeExpanded(false);
    } catch (e) {
      Alert.alert('Error', e.message || 'Failed to save focus exercises');
    } finally {
      setFocusModeSaving(false);
    }
  };

  const handleSettingsSave = async () => {
    if (!user?.id) return;
    setSettingsSaving(true);
    try {
      const res = await fetch(`${getApiUrl()}/api/users/${encodeURIComponent(String(user.id))}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          is_US_user: draftIsUSUser,
          showGettingStarted: draftShowGettingStarted,
          weightInKg: draftWeightInKg,
          ...(isSuperUser(user) ? { demoMode: draftDemoMode } : {}),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        Alert.alert('Could not save', data.error || 'Failed to update settings');
        return;
      }
      const updated = normalizeStudentUserRecord({
        ...user,
        ...data,
        is_US_user: draftIsUSUser,
        showGettingStarted: draftShowGettingStarted,
        weightInKg: draftWeightInKg,
        weight_in_kg: draftWeightInKg,
        demoMode: isSuperUser(user) ? draftDemoMode : isDemoMode(user),
        demo_mode: isSuperUser(user) ? draftDemoMode : isDemoMode(user),
      });
      setUser(updated);
      await setUserSnapshot(updated);
      setIsUSUserFromUser(updated);
      setWeightInKgFromUser(updated);
      setSettingsExpanded(false);
    } catch (e) {
      Alert.alert('Error', e.message || 'Failed to save settings');
    } finally {
      setSettingsSaving(false);
    }
  };
  const showTwoFactorSection =
    isOwnProfile && user?.is_two_factor_user === true && !isSuperUser(user);
  const twoFactorEnabled = Boolean(user?.two_factor_enabled_at);
  const twoFactorStatusLabel = twoFactorEnabled
    ? 'Authenticator is active'
    : 'Set up required on next login (or use reset below)';

  const handleProfileReset2fa = async ({ password, otp }) => {
    if (!user?.email) {
      Alert.alert('Error', 'No email on this account.');
      return;
    }
    setReset2faBusy(true);
    try {
      const body = { email: user.email, password };
      if (twoFactorEnabled && otp) body.token = otp;
      const res = await fetch(`${getApiUrl()}/api/auth/2fa/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        Alert.alert('Could not reset', data.error || 'Reset failed');
        return;
      }
      setShow2faResetModal(false);
      await fetchUser(user.id);
      Alert.alert(
        'Authenticator reset',
        data.message ||
          'Backup codes and enrollment were cleared. Sign in again to scan a new QR code on your phone.'
      );
    } catch (e) {
      Alert.alert('Error', e.message || 'Reset failed');
    } finally {
      setReset2faBusy(false);
    }
  };

  const styles = getStyles(theme);

  if (loading) {
    return (
      <View style={styles.container}>
        <Text style={styles.loadingText}>Loading...</Text>
      </View>
    );
  }

  if (!user) {
    return (
      <View style={styles.container}>
        <Text style={styles.errorText}>User not found</Text>
      </View>
    );
  }

  const displayImage = pendingProfile?.uri || profileImage;

  const profileSaveDisabled = profilePicSaving || !pendingProfile;

  const profileActionWidth = Math.min(280, Math.floor(width * 0.72));

  const gradeUrlStored =
    user?.grade_url != null && String(user.grade_url).trim() !== ''
      ? String(user.grade_url).trim()
      : null;
  const gradeDisplayUri = pendingGrade?.uri || gradeUrlStored;
  const hasGradeModalSource = Boolean(gradeDisplayUri);

  const renderGradePhotoInner = () => {
    if (gradeDisplayUri) {
      return (
        <Image
          source={{ uri: gradeDisplayUri }}
          style={styles.gradePhoto}
          contentFit="contain"
        />
      );
    }
    return (
      <View style={styles.gradePhotoPlaceholder}>
        <Text style={styles.gradePhotoPlaceholderText}>
          Upload your grade image here
        </Text>
      </View>
    );
  };

  const renderGradeModalInner = () => {
    if (!gradeDisplayUri) return null;
    return (
      <Image
        source={{ uri: gradeDisplayUri }}
        style={styles.modalImage}
        contentFit="contain"
      />
    );
  };

  return (
    <View
      style={styles.container}
      data-screen="profile"
      data-theme={theme.isDarkMode ? 'dark' : 'light'}
    >
      <StatusBar style={theme.isDarkMode ? 'light' : 'dark'} />
      
      {/* Header */}
      <View style={styles.header} data-header="true">
        <View style={styles.hamburgerContainer}>
          <HamburgerMenu navigation={navigation} stopwatchRef={stopwatchRef} />
        </View>
        <View style={styles.breadcrumbContainer}>
          <Text style={styles.breadcrumb}>Profile</Text>
        </View>
      </View>

      <ScrollView style={styles.content} contentContainerStyle={styles.contentContainer}>
        {/* Central Div - 80% width */}
        <View style={styles.centralDiv}>
          {/* Profile Picture Section */}
          <View style={styles.profilePicSection}>
            <TouchableOpacity
              style={styles.profilePicContainer}
              onPress={handlePickImage}
              activeOpacity={0.8}
              disabled={profilePicSaving}
            >
              {displayImage ? (
                <Image
                  source={{ uri: displayImage }}
                  style={styles.profilePic}
                  contentFit="cover"
                />
              ) : (
                <View style={styles.profilePicPlaceholder}>
                  <Text style={styles.profilePicPlaceholderText}>
                    {user.student_name ? user.student_name.charAt(0).toUpperCase() : 'U'}
                  </Text>
                </View>
              )}
            </TouchableOpacity>
            <View style={styles.profileUploadColumn}>
              <TouchableOpacity
                style={[styles.uploadPhotoButton, { width: profileActionWidth }]}
                onPress={handlePickImage}
                activeOpacity={0.8}
                disabled={profilePicSaving}
              >
                <Text style={styles.uploadPhotoButtonText}>Upload photo</Text>
              </TouchableOpacity>
              {pendingProfile ? (
                <View style={[styles.pendingFileRow, { width: profileActionWidth }]}>
                  <Text style={styles.pendingFileName} numberOfLines={2}>
                    {pendingProfile.fileName}
                  </Text>
                </View>
              ) : null}
              <TouchableOpacity
                style={[
                  styles.profileSaveButton,
                  { width: profileActionWidth },
                  !pendingProfile &&
                    !profilePicJustSaved &&
                    styles.profileSaveButtonDisabled,
                  pendingProfile && !profilePicSaving && styles.profileSaveButtonReady,
                  profilePicJustSaved &&
                    !pendingProfile &&
                    !profilePicSaving &&
                    styles.profileSaveButtonSuccess,
                ]}
                onPress={handleSaveProfilePicture}
                disabled={profileSaveDisabled}
              >
                {profilePicSaving ? (
                  <ActivityIndicator color="#FFFFFF" />
                ) : (
                  <Text
                    style={[
                      styles.profileSaveButtonText,
                      !pendingProfile &&
                        !profilePicJustSaved &&
                        styles.profileSaveButtonTextDisabled,
                    ]}
                  >
                    Save
                  </Text>
                )}
              </TouchableOpacity>
            </View>
          </View>

          {/* Student Full Name */}
          <Text style={styles.studentName}>
            {user.student_name || user.username || 'User'}
          </Text>

          {/* Current Grade */}
          <Text style={styles.currentGrade}>
            {user.student_grade || 'No Grade'}
          </Text>

          {/* Breaks Remaining */}
          {breaksRemaining !== null && (
            <Text style={styles.breaksRemaining}>
              {breaksRemaining === 'Max Grade' 
                ? 'Maximum Grade Achieved'
                : `${breaksRemaining} breaks remaining for next grade`
              }
            </Text>
          )}

          {/* Grade Photo */}
          <TouchableOpacity
            style={styles.gradePhotoContainer}
            onPress={() => {
              if (hasGradeModalSource) {
                setShowGradePhotoModal(true);
              }
            }}
            activeOpacity={0.8}
          >
            {renderGradePhotoInner()}
          </TouchableOpacity>

          <View style={styles.gradeUploadSection}>
            <View style={styles.gradeImageActionsRow}>
              <View style={styles.gradeUploadLeftColumn}>
                <TouchableOpacity
                  style={styles.updateGradeImageButton}
                  onPress={handlePickGradeImage}
                  activeOpacity={0.8}
                  disabled={gradeImageSaving}
                >
                  <Text style={styles.updateGradeImageButtonText}>Update Grade Image</Text>
                </TouchableOpacity>
                {pendingGrade ? (
                  <View style={styles.pendingGradeFileRow}>
                    <Text style={styles.pendingFileName} numberOfLines={2}>
                      {pendingGrade.fileName}
                    </Text>
                  </View>
                ) : null}
              </View>
              <TouchableOpacity
                style={[
                  styles.gradeSaveButtonSide,
                  !pendingGrade &&
                    !gradeJustSaved &&
                    styles.gradeSaveButtonSideMuted,
                  pendingGrade && !gradeImageSaving && styles.gradeSaveButtonSideReady,
                  gradeJustSaved &&
                    !pendingGrade &&
                    !gradeImageSaving &&
                    styles.gradeSaveButtonSideSuccess,
                ]}
                onPress={handleSaveGradeImage}
                disabled={gradeImageSaving || !pendingGrade}
                activeOpacity={0.8}
              >
                {gradeImageSaving ? (
                  <ActivityIndicator color="#FFFFFF" />
                ) : (
                  <Text
                    style={[
                      styles.gradeSaveButtonSideText,
                      !pendingGrade &&
                        !gradeJustSaved &&
                        styles.gradeSaveButtonSideTextMuted,
                    ]}
                  >
                    Save
                  </Text>
                )}
              </TouchableOpacity>
            </View>
          </View>

          <StudentGallerySection
            studentName={user.student_name || user.username}
            isOwnProfile={isOwnProfile}
            sectionWidth={width * 0.8}
          />

          {isOwnProfile ? (
            <View style={styles.settingsSection}>
              <TouchableOpacity
                style={styles.settingsHeaderRow}
                onPress={handleToggleSettings}
                activeOpacity={0.7}
                accessibilityRole="button"
                accessibilityState={{ expanded: settingsExpanded }}
                {...(Platform.OS === 'web' ? { dataSet: { profileSettingsHeader: 'true' } } : {})}
              >
                <MaterialIcons
                  name="settings"
                  size={22}
                  color={theme.colors.heading || '#d4af37'}
                  style={styles.settingsCogIcon}
                />
                <Text style={styles.settingsHeaderLabel}>Settings</Text>
                <MaterialIcons
                  name={settingsExpanded ? 'keyboard-arrow-up' : 'keyboard-arrow-down'}
                  size={26}
                  color={theme.colors.text}
                />
              </TouchableOpacity>

              {settingsExpanded ? (
                <View
                  style={styles.settingsPanel}
                  {...(Platform.OS === 'web'
                    ? { dataSet: { profileSettingsPanel: 'true' } }
                    : {})}
                >
                  <TouchableOpacity
                    style={styles.settingsCheckboxRow}
                    onPress={() => setDraftIsUSUser((v) => !v)}
                    activeOpacity={0.7}
                    accessibilityRole="checkbox"
                    accessibilityState={{ checked: draftIsUSUser }}
                    nativeID="profile-settings-checkbox-row"
                    {...(Platform.OS === 'web'
                      ? { dataSet: { profileSettingsCheckboxRow: 'true' } }
                      : {})}
                  >
                    <Text
                      style={styles.settingsCheckboxMark}
                      {...(Platform.OS === 'web'
                        ? { dataSet: { profileSettingsCheckboxMark: 'true' } }
                        : {})}
                    >
                      {draftIsUSUser ? '☑' : '☐'}
                    </Text>
                    <Text
                      style={styles.settingsCheckboxLabel}
                      {...(Platform.OS === 'web'
                        ? { dataSet: { profileSettingsCheckboxLabel: 'true' } }
                        : {})}
                    >
                      Dates in US format YYYY-DD-MM
                    </Text>
                  </TouchableOpacity>

                  <View
                    style={styles.settingsWeightToggleWrap}
                    {...(Platform.OS === 'web'
                      ? { dataSet: { profileSettingsWeightToggle: 'true' } }
                      : {})}
                  >
                    <Text style={styles.settingsWeightToggleLabel}>Weight units</Text>
                    <View style={styles.settingsSegmentedToggle}>
                      <TouchableOpacity
                        style={[
                          styles.settingsSegmentedOption,
                          draftWeightInKg && styles.settingsSegmentedOptionActive,
                        ]}
                        onPress={() => setDraftWeightInKg(true)}
                        activeOpacity={0.8}
                        accessibilityRole="button"
                        accessibilityState={{ selected: draftWeightInKg }}
                      >
                        <Text
                          style={[
                            styles.settingsSegmentedText,
                            draftWeightInKg && styles.settingsSegmentedTextActive,
                          ]}
                        >
                          KG
                        </Text>
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={[
                          styles.settingsSegmentedOption,
                          !draftWeightInKg && styles.settingsSegmentedOptionActive,
                        ]}
                        onPress={() => setDraftWeightInKg(false)}
                        activeOpacity={0.8}
                        accessibilityRole="button"
                        accessibilityState={{ selected: !draftWeightInKg }}
                      >
                        <Text
                          style={[
                            styles.settingsSegmentedText,
                            !draftWeightInKg && styles.settingsSegmentedTextActive,
                          ]}
                        >
                          lbs
                        </Text>
                      </TouchableOpacity>
                    </View>
                  </View>

                  <TouchableOpacity
                    style={styles.settingsCheckboxRow}
                    onPress={() => setDraftShowGettingStarted((v) => !v)}
                    activeOpacity={0.7}
                    accessibilityRole="checkbox"
                    accessibilityState={{ checked: draftShowGettingStarted }}
                    {...(Platform.OS === 'web'
                      ? { dataSet: { profileSettingsGettingStartedRow: 'true' } }
                      : {})}
                  >
                    <Text style={styles.settingsCheckboxMark}>
                      {draftShowGettingStarted ? '☑' : '☐'}
                    </Text>
                    <Text style={styles.settingsCheckboxLabel}>Show Getting Started Tile</Text>
                  </TouchableOpacity>

                  {isSuperUser(user) ? (
                    <TouchableOpacity
                      style={styles.settingsCheckboxRow}
                      onPress={() => setDraftDemoMode((v) => !v)}
                      activeOpacity={0.7}
                      accessibilityRole="checkbox"
                      accessibilityState={{ checked: draftDemoMode }}
                      {...(Platform.OS === 'web'
                        ? { dataSet: { profileSettingsDemoModeRow: 'true' } }
                        : {})}
                    >
                      <Text
                        style={styles.settingsCheckboxMark}
                        {...(Platform.OS === 'web'
                          ? { dataSet: { profileSettingsCheckboxMark: 'true' } }
                          : {})}
                      >
                        {draftDemoMode ? '☑' : '☐'}
                      </Text>
                      <Text
                        style={styles.settingsCheckboxLabel}
                        {...(Platform.OS === 'web'
                          ? { dataSet: { profileSettingsCheckboxLabel: 'true' } }
                          : {})}
                      >
                        Demo Mode
                      </Text>
                    </TouchableOpacity>
                  ) : null}

                  <View
                    style={styles.settingsActionsRow}
                    {...(Platform.OS === 'web'
                      ? { dataSet: { profileSettingsActionsRow: 'true' } }
                      : {})}
                  >
                    <TouchableOpacity
                      style={styles.settingsCancelButton}
                      onPress={handleSettingsCancel}
                      disabled={settingsSaving}
                      activeOpacity={0.8}
                      {...(Platform.OS === 'web'
                        ? { dataSet: { profileSettingsCancel: 'true' } }
                        : {})}
                    >
                      <Text style={styles.settingsCancelButtonText}>Cancel</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[
                        styles.settingsSaveButton,
                        settingsSaving && styles.settingsSaveButtonDisabled,
                      ]}
                      onPress={() => void handleSettingsSave()}
                      disabled={settingsSaving}
                      activeOpacity={0.8}
                      {...(Platform.OS === 'web'
                        ? { dataSet: { profileSettingsSave: 'true' } }
                        : {})}
                    >
                      {settingsSaving ? (
                        <ActivityIndicator color="#FFFFFF" size="small" />
                      ) : (
                        <Text style={styles.settingsSaveButtonText}>Save</Text>
                      )}
                    </TouchableOpacity>
                  </View>
                </View>
              ) : null}
            </View>
          ) : null}

          {isOwnProfile ? (
            <View style={styles.settingsSection}>
              <TouchableOpacity
                style={styles.settingsHeaderRow}
                onPress={handleToggleFocusMode}
                activeOpacity={0.7}
                accessibilityRole="button"
                accessibilityState={{ expanded: focusModeExpanded }}
                {...(Platform.OS === 'web' ? { dataSet: { profileFocusModeHeader: 'true' } } : {})}
              >
                <MaterialIcons
                  name="center-focus-strong"
                  size={22}
                  color={theme.colors.heading || '#d4af37'}
                  style={styles.settingsCogIcon}
                />
                <Text style={styles.settingsHeaderLabel}>Focus mode</Text>
                <MaterialIcons
                  name={focusModeExpanded ? 'keyboard-arrow-up' : 'keyboard-arrow-down'}
                  size={26}
                  color={theme.colors.text}
                />
              </TouchableOpacity>

              {focusModeExpanded ? (
                <View
                  style={styles.settingsPanel}
                  {...(Platform.OS === 'web'
                    ? { dataSet: { profileFocusModePanel: 'true' } }
                    : {})}
                >
                  <Text style={styles.focusModeIntro}>
                    Select exercises to focus on, on the homepage:
                  </Text>

                  <View
                    style={styles.focusModePlacementWrap}
                    {...(Platform.OS === 'web'
                      ? { dataSet: { profileFocusModePlacement: 'true' } }
                      : {})}
                  >
                    <Text style={styles.focusModePlacementLabel}>
                      Pick where you want the Focus reporting to go:
                    </Text>
                    {[
                      { value: 'top', label: 'Top' },
                      { value: 'bottom', label: 'Bottom' },
                    ].map((option) => {
                      const selected = draftFocusReportingPosition === option.value;
                      return (
                        <TouchableOpacity
                          key={option.value}
                          style={styles.settingsCheckboxRow}
                          onPress={() => setDraftFocusReportingPosition(option.value)}
                          activeOpacity={0.7}
                          accessibilityRole="radio"
                          accessibilityState={{ selected }}
                          {...(Platform.OS === 'web'
                            ? { dataSet: { profileFocusModePlacementOption: option.value } }
                            : {})}
                        >
                          <Text style={styles.settingsCheckboxMark}>{selected ? '◉' : '○'}</Text>
                          <Text style={styles.settingsCheckboxLabel}>{option.label}</Text>
                        </TouchableOpacity>
                      );
                    })}
                  </View>

                  {draftFocusSlots.map((slot, index) => (
                    <FocusExercisePickerRow
                      key={`focus-slot-${index}`}
                      value={slot}
                      onChange={(next) =>
                        setDraftFocusSlots((prev) => {
                          const copy = [...prev];
                          copy[index] = next;
                          return copy;
                        })
                      }
                      onRemove={() =>
                        setDraftFocusSlots((prev) => prev.filter((_, i) => i !== index))
                      }
                      showRemove={draftFocusSlots.length > 1}
                      studentId={user?.id}
                      courses={focusCourses}
                      theme={theme}
                    />
                  ))}

                  {draftFocusSlots.length < MAX_FOCUS_EXERCISES ? (
                    <TouchableOpacity
                      style={styles.focusModeAddRow}
                      onPress={() => setDraftFocusSlots((prev) => [...prev, null])}
                      activeOpacity={0.8}
                      {...(Platform.OS === 'web' ? { dataSet: { profileFocusModeAdd: 'true' } } : {})}
                    >
                      <MaterialIcons
                        name="add-circle-outline"
                        size={22}
                        color={theme.colors.heading || '#d4af37'}
                      />
                      <Text style={styles.focusModeAddText}>Add exercise</Text>
                    </TouchableOpacity>
                  ) : null}

                  <View
                    style={styles.settingsActionsRow}
                    {...(Platform.OS === 'web'
                      ? { dataSet: { profileFocusModeActionsRow: 'true' } }
                      : {})}
                  >
                    <TouchableOpacity
                      style={styles.settingsCancelButton}
                      onPress={handleFocusModeCancel}
                      disabled={focusModeSaving}
                      activeOpacity={0.8}
                    >
                      <Text style={styles.settingsCancelButtonText}>Cancel</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[
                        styles.settingsSaveButton,
                        focusModeSaving && styles.settingsSaveButtonDisabled,
                      ]}
                      onPress={() => void handleFocusModeSave()}
                      disabled={focusModeSaving}
                      activeOpacity={0.8}
                      {...(Platform.OS === 'web'
                        ? { dataSet: { profileFocusModeSave: 'true' } }
                        : {})}
                    >
                      {focusModeSaving ? (
                        <ActivityIndicator color="#FFFFFF" size="small" />
                      ) : (
                        <Text style={styles.settingsSaveButtonText}>Save</Text>
                      )}
                    </TouchableOpacity>
                  </View>
                </View>
              ) : null}
            </View>
          ) : null}

          {showTwoFactorSection ? (
            <View style={styles.twoFactorSection}>
              <Text style={styles.twoFactorHeading}>Two-factor authentication</Text>
              <Text style={styles.twoFactorStatus}>{twoFactorStatusLabel}</Text>
              <Text style={styles.twoFactorHint}>
                Reset clears your authenticator secret, backup codes, and enrollment timestamps. Use
                this when you change phones or need to scan a new QR code. You can repeat this
                whenever you test or switch devices.
              </Text>
              <TouchableOpacity
                style={styles.twoFactorResetButton}
                onPress={() => setShow2faResetModal(true)}
                activeOpacity={0.85}
              >
                <Text style={styles.twoFactorResetButtonText}>Reset authenticator</Text>
              </TouchableOpacity>
            </View>
          ) : null}
        </View>
      </ScrollView>

      <TwoFactorResetModal
        visible={show2faResetModal}
        onClose={() => !reset2faBusy && setShow2faResetModal(false)}
        title="Reset authenticator"
        description={
          twoFactorEnabled
            ? 'Enter your password and a current code from your authenticator app (or use login reset with password only during sign-in).'
            : 'Enter your password to clear any partial setup before signing in again.'
        }
        requireOtp={twoFactorEnabled}
        onSubmit={handleProfileReset2fa}
        busy={reset2faBusy}
      />

      {/* Grade Photo Modal */}
      <Modal
        visible={showGradePhotoModal}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowGradePhotoModal(false)}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowGradePhotoModal(false)}
        >
          <View style={styles.modalContent} onStartShouldSetResponder={() => true}>
            {/* Close Button */}
            <TouchableOpacity
              style={styles.closeButton}
              onPress={() => setShowGradePhotoModal(false)}
              activeOpacity={0.7}
            >
              <Text style={styles.closeButtonText}>✕</Text>
            </TouchableOpacity>
            
            {renderGradeModalInner()}
          </View>
        </TouchableOpacity>
      </Modal>
      <Stopwatch ref={stopwatchRef} />
    </View>
  );
}

const getStyles = (theme) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: 50,
    paddingBottom: 20,
    paddingHorizontal: 20,
    backgroundColor: theme.colors.background,
  },
  hamburgerContainer: {
    marginRight: 15,
    backgroundColor: 'transparent',
  },
  breadcrumbContainer: {
    flex: 1,
  },
  breadcrumb: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.colors.heading,
  },
  content: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  contentContainer: {
    padding: 20,
    paddingBottom: 40,
    alignItems: 'center',
    backgroundColor: theme.colors.background,
  },
  centralDiv: {
    width: width * 0.8,
    alignItems: 'center',
  },
  profilePicSection: {
    width: '100%',
    alignItems: 'center',
    alignSelf: 'stretch',
    marginBottom: 12,
  },
  profilePicContainer: {
    alignSelf: 'center',
    marginBottom: 12,
    cursor: Platform.OS === 'web' ? 'pointer' : 'default',
  },
  profilePic: {
    width: 120,
    height: 120,
    borderRadius: 60,
    borderWidth: 3,
    borderColor: '#d4af37',
  },
  profilePicPlaceholder: {
    width: 120,
    height: 120,
    borderRadius: 60,
    backgroundColor: theme.colors.primary || '#d4af37',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 3,
    borderColor: '#d4af37',
  },
  profilePicPlaceholderText: {
    color: '#FFFFFF',
    fontSize: 48,
    fontWeight: 'bold',
  },
  uploadPhotoButton: {
    backgroundColor: theme.colors.primary || '#d4af37',
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 8,
    alignItems: 'center',
    marginBottom: 10,
  },
  uploadPhotoButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  pendingFileRow: {
    marginTop: 4,
    marginBottom: 10,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.border,
    backgroundColor: theme.colors.surface,
  },
  pendingFileName: {
    fontSize: 14,
    color: theme.colors.text,
    textAlign: 'center',
  },
  profileSaveButton: {
    paddingVertical: 15,
    paddingHorizontal: 24,
    borderRadius: 8,
    minWidth: 150,
    alignItems: 'center',
    alignSelf: 'stretch',
    maxWidth: 280,
    width: '100%',
    marginBottom: 30,
    backgroundColor: '#999999',
  },
  profileSaveButtonDisabled: {
    opacity: 0.55,
  },
  profileSaveButtonReady: {
    backgroundColor: theme.colors.primary || '#d4af37',
    opacity: 1,
  },
  profileSaveButtonSuccess: {
    backgroundColor: '#22c55e',
    opacity: 1,
  },
  profileSaveButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  profileSaveButtonTextDisabled: {
    color: '#E8E8E8',
  },
  studentName: {
    fontSize: 24,
    fontWeight: 'bold',
    color: theme.colors.heading,
    marginBottom: 10,
    textAlign: 'center',
  },
  currentGrade: {
    fontSize: 20,
    fontWeight: '600',
    color: theme.colors.text,
    marginBottom: 10,
    textAlign: 'center',
  },
  breaksRemaining: {
    fontSize: 16,
    color: theme.colors.text,
    marginBottom: 30,
    textAlign: 'center',
  },
  gradePhotoContainer: {
    width: '100%',
    alignItems: 'center',
    marginTop: 20,
  },
  gradePhotoPlaceholder: {
    width: '100%',
    height: 300,
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#F5F5F5',
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: theme.colors.border,
    borderStyle: 'dashed',
  },
  gradePhotoPlaceholderText: {
    color: theme.colors.text,
    fontSize: 16,
    textAlign: 'center',
    opacity: 0.6,
  },
  gradePhoto: {
    width: '100%',
    height: 300,
    borderRadius: 8,
  },
  gradeUploadSection: {
    width: '100%',
    alignSelf: 'stretch',
    marginTop: 16,
    marginBottom: 8,
  },
  gradeImageActionsRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    width: '100%',
  },
  gradeUploadLeftColumn: {
    flex: 1,
    marginRight: 10,
  },
  updateGradeImageButton: {
    width: '100%',
    backgroundColor: theme.colors.primary || '#d4af37',
    paddingVertical: 14,
    paddingHorizontal: 12,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  updateGradeImageButtonText: {
    color: '#FFFFFF',
    fontSize: 15,
    fontWeight: '600',
    textAlign: 'center',
  },
  gradeSaveButtonSide: {
    minWidth: 96,
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#999999',
  },
  gradeSaveButtonSideMuted: {
    opacity: 0.55,
  },
  gradeSaveButtonSideReady: {
    backgroundColor: theme.colors.primary || '#d4af37',
    opacity: 1,
  },
  gradeSaveButtonSideSuccess: {
    backgroundColor: '#22c55e',
    opacity: 1,
  },
  gradeSaveButtonSideText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  gradeSaveButtonSideTextMuted: {
    color: '#E8E8E8',
  },
  settingsSection: {
    width: '100%',
    alignSelf: 'stretch',
    marginTop: 24,
    marginBottom: 8,
  },
  settingsHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
  },
  settingsCogIcon: {
    marginRight: 10,
  },
  settingsHeaderLabel: {
    flex: 1,
    fontSize: 18,
    fontWeight: '600',
    color: theme.colors.heading,
  },
  settingsPanel: {
    marginTop: 12,
    padding: 16,
    borderRadius: theme.isDarkMode ? 0 : 12,
    borderWidth: theme.isDarkMode ? 2 : 3,
    borderColor: theme.isDarkMode ? theme.colors.border : '#d4af37',
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#faf8f4',
    ...(Platform.OS === 'web'
      ? { boxShadow: theme.isDarkMode ? 'none' : '0 2px 8px rgba(212, 175, 55, 0.25)' }
      : {
          shadowColor: theme.isDarkMode ? 'transparent' : '#d4af37',
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: theme.isDarkMode ? 0 : 0.25,
          shadowRadius: 4,
          elevation: theme.isDarkMode ? 0 : 3,
        }),
  },
  settingsCheckboxRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 16,
    backgroundColor: 'transparent',
  },
  settingsCheckboxMark: {
    fontSize: 20,
    marginRight: 10,
    color: theme.colors.heading || '#d4af37',
    lineHeight: 22,
    backgroundColor: 'transparent',
  },
  settingsCheckboxLabel: {
    flex: 1,
    fontSize: 15,
    color: theme.colors.text,
    lineHeight: 22,
    backgroundColor: 'transparent',
  },
  settingsWeightToggleWrap: {
    marginBottom: 16,
    backgroundColor: 'transparent',
  },
  settingsWeightToggleLabel: {
    fontSize: 15,
    color: theme.colors.text,
    marginBottom: 8,
    backgroundColor: 'transparent',
  },
  settingsSegmentedToggle: {
    flexDirection: 'row',
    alignSelf: 'flex-start',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.isDarkMode ? theme.colors.border : '#d4af37',
    overflow: 'hidden',
    backgroundColor: theme.isDarkMode ? '#1a1a1a' : '#ffffff',
  },
  settingsSegmentedOption: {
    paddingVertical: 8,
    paddingHorizontal: 20,
    backgroundColor: 'transparent',
  },
  settingsSegmentedOptionActive: {
    backgroundColor: theme.colors.primary || '#d4af37',
  },
  settingsSegmentedText: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.text,
    backgroundColor: 'transparent',
  },
  settingsSegmentedTextActive: {
    color: theme.isDarkMode ? '#1a1a1a' : '#000000',
  },
  settingsActionsRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    backgroundColor: 'transparent',
  },
  settingsCancelButton: {
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    backgroundColor: '#6b7280',
    minWidth: 100,
    alignItems: 'center',
  },
  settingsCancelButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  settingsSaveButton: {
    marginLeft: 12,
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    backgroundColor: theme.colors.primary || '#d4af37',
    minWidth: 100,
    alignItems: 'center',
  },
  settingsSaveButtonDisabled: {
    opacity: 0.7,
  },
  focusModeIntro: {
    fontSize: 15,
    color: theme.colors.text,
    lineHeight: 22,
    marginBottom: 16,
    backgroundColor: 'transparent',
  },
  focusModePlacementWrap: {
    marginBottom: 16,
    backgroundColor: 'transparent',
  },
  focusModePlacementLabel: {
    fontSize: 15,
    color: theme.colors.text,
    lineHeight: 22,
    marginBottom: 8,
    backgroundColor: 'transparent',
  },
  focusModeAddRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 16,
    backgroundColor: 'transparent',
  },
  focusModeAddText: {
    fontSize: 15,
    fontWeight: '600',
    color: theme.colors.heading || '#d4af37',
    backgroundColor: 'transparent',
  },
  settingsSaveButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  pendingGradeFileRow: {
    width: '100%',
    marginTop: 10,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.border,
    backgroundColor: theme.colors.surface,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    width: width * 0.9,
    height: width * 0.9,
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  closeButton: {
    position: 'absolute',
    top: 10,
    right: 10,
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10,
  },
  closeButtonText: {
    color: '#FFFFFF',
    fontSize: 24,
    fontWeight: 'bold',
  },
  modalImage: {
    width: '100%',
    height: '100%',
  },
  loadingText: {
    color: theme.colors.text,
    fontSize: 18,
    textAlign: 'center',
    marginTop: 50,
  },
  errorText: {
    color: '#FF0000',
    fontSize: 18,
    textAlign: 'center',
    marginTop: 50,
  },
  twoFactorSection: {
    width: '100%',
    marginTop: 28,
    paddingTop: 20,
    borderTopWidth: 1,
    borderTopColor: theme.colors.border,
  },
  twoFactorHeading: {
    fontSize: 18,
    fontWeight: '700',
    color: theme.colors.heading,
    marginBottom: 8,
  },
  twoFactorStatus: {
    fontSize: 15,
    color: theme.colors.text,
    marginBottom: 8,
  },
  twoFactorHint: {
    fontSize: 13,
    lineHeight: 19,
    color: theme.colors.text,
    opacity: 0.85,
    marginBottom: 14,
  },
  twoFactorResetButton: {
    alignSelf: 'flex-start',
    backgroundColor: theme.colors.primary || '#d4af37',
    paddingVertical: 12,
    paddingHorizontal: 18,
    borderRadius: 8,
  },
  twoFactorResetButtonText: {
    color: '#FFFFFF',
    fontSize: 15,
    fontWeight: '700',
  },
});
